import React from "react";
import { UserWord, UserWordStatus } from "@shared_types";
import { ES, GB } from "country-flag-icons/react/3x2";
import { FaPlus, FaTrash, FaTimesCircle, FaCheckCircle } from "react-icons/fa";
import ToggleButton from "../Buttons/ToggleButton";

interface WordCardProps {
  word: UserWord;
  viewType: "known" | "learning";
  effectiveStatus: UserWordStatus;
  deleteConfirmId: number | string | null;
  onStatusChange: (word: UserWord, newStatus: UserWordStatus) => void;
  onDeleteClick: (wordId: number) => void;
  onCancelDelete: () => void;
  lastWordRef?: React.Ref<HTMLDivElement>;
}

const WordCard: React.FC<WordCardProps> = ({
  word,
  viewType,
  effectiveStatus,
  deleteConfirmId,
  onStatusChange,
  onDeleteClick,
  onCancelDelete,
  lastWordRef,
}) => {
  let activeIcon = <FaPlus className="w-2 h-2" />;
  let inactiveIcon = <FaPlus className="w-2 h-2" />;
  let activeText = "to known";
  let inactiveText = "to learning";
  let activeColor = "";
  let inactiveColor = "";

  if (viewType === "learning") {
    activeIcon = <FaCheckCircle className="w-2 h-2" />;
    inactiveIcon = <FaPlus className="w-2 h-2" />;
    activeText = "known";
    inactiveText = "to known";
    activeColor = "text-[var(--color-green)]";
    inactiveColor = "";
  } else {
    activeIcon = <FaPlus className="w-2 h-2" />;
    activeColor = "";
    inactiveIcon = <FaCheckCircle className="w-2 h-2" />;
    inactiveColor = "text-[var(--color-green)]";
    activeText = "to learning";
    inactiveText = "learning";
  }

  return (
    <div
      ref={lastWordRef}
      className={`flex flex-col p-3 bg-white rounded-xl ${
        effectiveStatus === "known" ? "" : ""
      } gap-3`}
    >
      {/* Spanish word row */}
      <div className="flex items-center w-full">
        <ES
          title="Spain"
          className="w-5 h-5 mr-2 flex-shrink-0 hidden xs:block"
        />
        <div className="font-bold break-words text-left text-base flex-grow">
          <span
            className={`${
              effectiveStatus === "known"
                ? ""
                : "bg-[var(--color-light-yellow)] rounded-md px-[3px]"
            }`}
          >
            {word.targetWord}
          </span>
        </div>
      </div>

      {/* English translation row with buttons */}
      <div className="flex items-center w-full">
        <GB
          title="Great Britain"
          className="w-5 h-5 mr-2 flex-shrink-0 hidden xs:block"
        />
        <div className="break-words text-left text-base text-[var(--color-grey)] flex-grow">
          {word.nativeWord}
        </div>
        <div className="flex flex-wrap gap-1 ml-2">
          <ToggleButton
            isActive={effectiveStatus === "known"}
            activeText={activeText}
            inactiveText={inactiveText}
            activeColor={activeColor}
            activeIcon={activeIcon}
            inactiveColor={inactiveColor}
            inactiveIcon={inactiveIcon}
            disableAnimation={true}
            onClick={() =>
              onStatusChange(
                word,
                effectiveStatus === "known" ? "learning" : "known"
              )
            }
            className={`text-xs px-2 py-1 rounded-full transition-colors`}
          />
          {deleteConfirmId === word.userWordId ? (
            <div className="flex gap-1">
              <button
                onClick={() => onDeleteClick(word.userWordId)}
                className="text-xs text-white bg-[var(--color-red)] border border-[var(--color-red)] px-2 py-1 rounded-full transition-colors flex items-center"
              >
                <FaCheckCircle className="w-3 h-3" />
              </button>
              <button
                onClick={onCancelDelete}
                className="text-xs text-[var(--color-grey)] border border-[var(--color-grey-light)] px-2 py-1 rounded-full transition-colors flex items-center"
              >
                <FaTimesCircle className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => onDeleteClick(word.userWordId)}
              className="text-xs border border-[var(--color-grey-light)] px-2 py-1 rounded-full transition-colors flex items-center"
            >
              <FaTrash className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WordCard;
